import React from 'react'
import { useLocation } from 'react-router-dom'
import { WifiOff } from 'lucide-react'
import { useBackendHealth } from '@/hooks/useBackendHealth'
import { useAuthStore } from '@/stores/authStore'
import { formatClockTime } from '@/lib/format'
import { useLoadGapStore } from '@/stores/loadGapStore'

/**
 * "You're offline" row for the app shell.
 *
 * - Driven only by real network loss (browser offline event, or requests that
 *   got no response). A 5xx is a server fault and is shown by the screen's own
 *   error card — it never says "Connection lost" here (UX-016).
 * - Rendered IN FLOW inside Layout's banner stack (below the phone top bar,
 *   above the page), on a solid surface — it never overlaps the header.
 * - Says how old the data on screen is, so cached lists don't pass for live.
 */
export function OfflineBanner(): React.ReactElement | null {
  const { status, lastSuccess } = useBackendHealth()
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  // Cards on this screen that couldn't load because of the network.
  const gaps = useLoadGapStore((s) => s.offlineGaps)

  // Suppress on public auth routes — login itself surfaces the failure.
  const isAuthRoute =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/forgot-password') ||
    location.pathname.startsWith('/reset-password')

  if (isAuthRoute || !isAuthenticated) return null
  if (status !== 'offline') return null

  const updated = lastSuccess ? formatClockTime(lastSuccess) : null

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      className="mx-4 mt-3 md:mx-6 flex items-center gap-3 rounded-2xl px-4 py-3 text-[14px] leading-snug"
      style={{
        background: 'var(--midnight-surface, var(--surface-0))',
        border: '0.5px solid rgb(var(--blush-rgb) / 0.40)',
        color: 'var(--midnight-text, rgb(var(--fg-rgb)))',
      }}
    >
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
        style={{ background: 'rgb(var(--blush-rgb) / 0.16)', color: 'var(--midnight-blush, var(--blush))' }}
      >
        <WifiOff className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="font-semibold">You're offline.</span>{' '}
        <span style={{ color: 'var(--midnight-text2)' }} data-testid="offline-banner-detail">
          {offlineBannerDetail(updated, gaps)}
        </span>
      </span>
    </div>
  )
}

/**
 * The banner's second sentence, true to what the page shows. With nothing
 * missing it dates the data on screen; when part of the page couldn't load it
 * says so, matching the "You're offline" card below it.
 */
export function offlineBannerDetail(updated: string | null, gaps: number): string {
  const save = "Changes can't be saved until you're back online."
  if (gaps > 0) {
    return `${gaps === 1 ? 'Part of this page needs' : 'Parts of this page need'} a connection to load. ${save}`
  }
  return `${updated ? `Showing what was loaded at ${updated}.` : 'Showing what was already loaded.'} ${save}`
}

export default OfflineBanner
